import { RegexMatch } from "@/data/enums/router";
import { DocCategories } from "@/data/enums/docs";
import { SegmentTypes } from "@/data/enums/segments";
import type { Route } from "vue-router";

import CProdsListing from "@/components/app/global/contractProducts/cProdsListing.vue";
import SegmentResultsListing from "@/components/app/admin/segments/segmentResultsListing.vue";
import { ContractStatusCodes } from "@/models/contracts";

import {
  DefaultCProdFilters,
  statusParam,
  IsSubscriptionParam,
  isFromLast30DaysParam,
  IsCategoryParam,
  hasUnresolvedRequestsParam,
  IsSubscriptionFilter,
  HasProvisionIssuesFilter
} from "@/data/filters/contractProducts";
import {
  DateCancelledSorter,
  DatePurchasedSorter,
  DefaultCProdSorters,
  StatusSorter
} from "@/data/sorters/contractProducts";

import { ListDisplayMode } from "@/data/constants";
import { UserMetaKeys } from "@/data/enums/users";

export default [
  {
    path: "subscriptions",
    name: "adminSubscriptions",
    redirect: { name: "adminSubscriptionsList" },
    component: () =>
      import("@/views/admin/contractProducts/subscriptions/index.vue"),
    props: {
      usedFilters: () => [...DefaultCProdFilters(), IsSubscriptionFilter()]
    },
    meta: {
      backTo: "_.subscriptions",
      categorySlug: DocCategories.USING_UPMIND,
      docsSlugs: ["product-management"]
    },
    children: [
      {
        path: "list",
        name: "adminSubscriptionsList",
        component: {
          name: "AdminSubscriptionsList",
          template: "<router-view />"
        },
        redirect: { name: "admin__allSubscriptions" },
        children: [
          {
            path: "all",
            name: "admin__allSubscriptions",
            component: CProdsListing,
            props: () => ({
              availableSorters: DefaultCProdSorters,
              availableFilters: () => [
                ...DefaultCProdFilters(),
                IsSubscriptionFilter()
              ],
              routeFilters: () => ({
                ...IsSubscriptionParam()
              }),
              segmentType: SegmentTypes.CONTRACTS_PRODUCTS_SUBSCRIPTION
            })
          },
          {
            path: "active",
            name: "admin__activeSubscriptions",
            component: CProdsListing,
            props: () => ({
              availableSorters: DefaultCProdSorters,
              availableFilters: () => [
                ...DefaultCProdFilters(["status.code"]),
                IsSubscriptionFilter()
              ],
              routeFilters: () => ({
                ...IsSubscriptionParam(),
                ...statusParam([ContractStatusCodes.ACTIVE])
              }),
              segmentType: SegmentTypes.CONTRACTS_PRODUCTS_SUBSCRIPTION
            })
          },
          {
            path: "awaiting-activation",
            name: "admin__awaitingActivationSubscriptions",
            component: CProdsListing,
            props: () => ({
              availableSorters: DefaultCProdSorters,
              availableFilters: () => [
                ...DefaultCProdFilters(["status.code"]),
                IsSubscriptionFilter()
              ],
              routeFilters: () => ({
                ...IsSubscriptionParam(),
                ...statusParam([ContractStatusCodes.AWAITING_ACTIVATION])
              }),
              segmentType: SegmentTypes.CONTRACTS_PRODUCTS_SUBSCRIPTION
            })
          },
          {
            path: "pending-summary",
            name: "admin__pendingSummarySubscriptions",
            component: CProdsListing,
            props: () => ({
              availableSorters: DefaultCProdSorters,
              availableFilters: () => [
                ...DefaultCProdFilters(["status.code"]),
                IsSubscriptionFilter()
              ],
              routeFilters: () => ({
                ...IsSubscriptionParam(),
                ...statusParam([ContractStatusCodes.PENDING])
              }),
              segmentType: SegmentTypes.CONTRACTS_PRODUCTS_SUBSCRIPTION
            })
          },
          {
            path: "suspended",
            name: "admin__suspendedSubscriptions",
            component: CProdsListing,
            props: () => ({
              availableSorters: DefaultCProdSorters,
              availableFilters: () => [
                ...DefaultCProdFilters(["status.code"]),
                IsSubscriptionFilter()
              ],
              routeFilters: () => ({
                ...IsSubscriptionParam(),
                ...statusParam([ContractStatusCodes.SUSPENDED])
              }),
              segmentType: SegmentTypes.CONTRACTS_PRODUCTS_SUBSCRIPTION
            })
          },
          {
            path: "cancelled",
            name: "admin__cancelledSubscriptions",
            component: CProdsListing,
            props: () => ({
              availableSorters: () => [
                StatusSorter(),
                DateCancelledSorter(),
                DatePurchasedSorter()
              ],
              availableFilters: () => [
                ...DefaultCProdFilters(["status.code"]),
                IsSubscriptionFilter()
              ],
              routeFilters: () => ({
                ...IsSubscriptionParam(),
                ...statusParam([ContractStatusCodes.CANCELLED])
              }),
              segmentType: SegmentTypes.CONTRACTS_PRODUCTS_SUBSCRIPTION
            })
          },
          {
            path: "lapsed",
            name: "admin__lapsedSubscriptions",
            component: CProdsListing,
            props: () => ({
              availableSorters: DefaultCProdSorters,
              availableFilters: () => [
                ...DefaultCProdFilters(["status.code"]),
                IsSubscriptionFilter()
              ],
              routeFilters: () => ({
                ...IsSubscriptionParam(),
                ...statusParam([ContractStatusCodes.CLOSED])
              }),
              segmentType: SegmentTypes.CONTRACTS_PRODUCTS_SUBSCRIPTION
            })
          },
          {
            path: "last-30-days",
            name: "admin__fromLast30DaysSubscriptions",
            component: CProdsListing,
            props: () => ({
              availableSorters: DefaultCProdSorters,
              availableFilters: () => [
                ...DefaultCProdFilters(["created_at"]),
                IsSubscriptionFilter()
              ],
              routeFilters: () => ({
                ...IsSubscriptionParam(),
                ...isFromLast30DaysParam()
              }),
              segmentType: SegmentTypes.CONTRACTS_PRODUCTS_SUBSCRIPTION
            })
          },
          {
            path: "has-provision-issues",
            name: "admin__hasProvisionIssuesSubscriptions",
            component: CProdsListing,
            props: {
              availableSorters: DefaultCProdSorters,
              availableFilters: () => [
                ...DefaultCProdFilters(),
                IsSubscriptionFilter(),
                HasProvisionIssuesFilter()
              ],
              routeFilters: () => ({
                ...IsSubscriptionParam(),
                ...hasUnresolvedRequestsParam()
              }),
              segmentType: SegmentTypes.CONTRACTS_PRODUCTS_SUBSCRIPTION
            }
          }
        ]
      },
      {
        path: `category/:categoryId(${RegexMatch.UUID})`,
        name: "admin__allSubscriptionsCategoryProducts",
        component: CProdsListing,
        props: {
          availableSorters: DefaultCProdSorters,
          availableFilters: () => [
            ...DefaultCProdFilters(["product.category.id"]),
            IsSubscriptionFilter()
          ],
          routeFilters: (vm: any) => ({
            ...IsSubscriptionParam(),
            ...IsCategoryParam(vm)
          }),
          segmentType: SegmentTypes.CONTRACTS_PRODUCTS_SUBSCRIPTION
        }
      },
      {
        path: `segment/:segmentId(${RegexMatch.UUID})`,
        name: "adminSubscriptionSegmentList",
        component: {
          name: "AdminSubscriptionsSegmentList",
          template: "<router-view />"
        },
        redirect: { name: "admin__subscriptionsSegment" },
        children: [
          {
            path: "",
            name: "admin__subscriptionsSegment",
            component: SegmentResultsListing,
            props: (route: Route) => ({
              defaultDisplayMode: ListDisplayMode.GRID,
              displayModeStoragePath: UserMetaKeys.UI_CPRODS_VIEW,
              segmentTypeHint: SegmentTypes.CONTRACTS_PRODUCTS_SUBSCRIPTION,
              segmentId: route.params.segmentId,
              newSegmentRouteName: "adminSubscriptionSegmentList",
              availableSorters: DefaultCProdSorters,
              availableFilters: () => [
                ...DefaultCProdFilters(),
                IsSubscriptionFilter()
              ]
            })
          }
        ]
      }
    ]
  }
];
